<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="estrut-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="estrut-cat-modal-title"
    >
      <div class="estrut-modal">
        <div class="estrut-modal__accent" aria-hidden="true" />

        <header class="estrut-modal__header">
          <div class="estrut-modal__icon estrut-modal__icon--accent">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </div>
          <div class="estrut-modal__titles">
            <h2 id="estrut-cat-modal-title" class="estrut-modal__title">
              {{ mode === 'edit' ? t('estruturacao.lessons.editCategory') : t('estruturacao.lessons.newCategory') }}
            </h2>
            <p class="estrut-modal__subtitle">{{ t('estruturacao.lessons.categoryTitleHint') }}</p>
          </div>
          <button type="button" class="estrut-modal__close" :aria-label="t('common.close')" @click="close">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <form class="estrut-modal__form" @submit.prevent="submit">
          <div class="estrut-modal__body">
            <div class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-cat-titulo">
                {{ t('estruturacao.lessons.categoryTitle') }}
                <span class="estrut-modal__required">*</span>
              </label>
              <input
                id="estrut-cat-titulo"
                v-model="form.titulo"
                type="text"
                class="estrut-modal__input"
                :placeholder="t('estruturacao.lessons.categoryTitlePlaceholder')"
                required
                autofocus
              />
            </div>

            <label v-if="mode === 'edit'" class="estrut-modal__toggle">
              <input v-model="form.ativo" type="checkbox" />
              <span class="estrut-modal__toggle-track" aria-hidden="true" />
              <span class="estrut-modal__toggle-text">
                <span class="estrut-modal__toggle-label">{{ t('estruturacao.lessons.categoryActive') }}</span>
              </span>
            </label>

            <p v-if="error" class="estrut-modal__error" role="alert">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ error }}
            </p>
          </div>

          <footer class="estrut-modal__footer">
            <button type="button" class="estrut-modal__btn estrut-modal__btn--secondary" @click="close">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              {{ t('common.cancel') }}
            </button>
            <button type="submit" class="estrut-modal__btn estrut-modal__btn--primary" :disabled="saving">
              <svg v-if="saving" class="estrut-modal__btn-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <svg v-else-if="mode === 'edit'" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ saving ? t('common.loading') : (mode === 'edit' ? t('common.save') : t('common.create')) }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  open: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },
  initial: { type: Object, default: null }
})

const emit = defineEmits(['close', 'submit'])

const { t } = useI18n()

const saving = ref(false)
const error = ref('')

const form = reactive({
  titulo: '',
  ativo: true
})

watch(
  () => [props.open, props.initial, props.mode],
  () => {
    if (!props.open) return
    error.value = ''
    saving.value = false
    form.titulo = props.initial?.titulo || ''
    form.ativo = props.initial?.ativo != null ? props.initial.ativo !== false : true
  },
  { immediate: true, deep: true }
)

function close() {
  emit('close')
}

function submit() {
  const titulo = form.titulo?.trim()
  if (!titulo) {
    error.value = t('estruturacao.lessons.validationTitleRequired')
    return
  }
  error.value = ''
  saving.value = true
  emit('submit', {
    titulo,
    ...(props.mode === 'edit' ? { ativo: form.ativo } : {})
  })
}

defineExpose({
  setSaving: (v) => { saving.value = v },
  setError: (msg) => { error.value = msg; saving.value = false }
})
</script>

<style src="./estruturacao-modals.css"></style>
